import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Flex,
  SelectField,
  SwitchField,
  TextAreaField,
  TextField,
  Text,
  Label,
} from "@aws-amplify/ui-react";
import { formatDate } from "../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { TagsInput } from "react-tag-input-component";
import { useNavigate, useParams } from "react-router-dom";
import { useImmunizationsPutMutation } from "./api/useImmunizationsMutation";
import { useImmunizationQuery } from "./api/useImmunizationsQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserQuery } from "../common/useUserQuery";
import { useEffect, useState } from "react";
const { z } = require("zod");

const putImmunizationsRequest = z.object({
  immunizationName: z
    .string({ message: "Name is Required" })
    .min(1, { message: "Name is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  immunizationDescription: z.string({ message: "Description is Required" }),
  administeredDates: z.array(z.string().date()).nullish(),
  administeredLocation: z
    .string({ message: "Administered Location is Required" })
    .min(1, { message: "Administered Location is Required" }),
  dosage: z
    .string({ message: "Dosage is Required" })
    .min(1, { message: "Dosage is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  method: z
    .string({ message: "Method is Required" })
    .min(1, { message: "Method is Required" }),
  detailsTags: z.optional(z.array(z.string())),
  isActive: z.optional(z.number().int()),
});

const requiredValidations = putImmunizationsRequest.required();

export default function ImmunizationsEditPage() {
  const [administeredDates, setAdministeredDates] = useState([]);
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();
  const params = useParams();
  const { data } = useImmunizationQuery(
    params.id,
    userQueryData?.patientUserId
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(requiredValidations),
  });
  const { mutate: immunizationPutMutation } = useImmunizationsPutMutation();

  useEffect(() => {
    if (data) {
      reset({
        immunizationName: data?.immunizationName,
        immunizationDescription: data?.immunizationDescription,
        administeredDates: JSON.parse(data?.administeredDates),
        administeredLocation: data?.administeredLocation,
        dosage: data?.dosage,
        method: data?.method,
        detailsTags: data?.detailsTags,
        isActive: data?.isActive,
      });
      setAdministeredDates(JSON.parse(data?.administeredDates));
    }
  }, [data]);

  const administeredDatesAdd = (administeredDate) => {
    const administeredDateFormatted = formatDate(new Date(administeredDate));
    if (!administeredDates.some((ad) => ad === administeredDateFormatted)) {
      setAdministeredDates((pre) => {
        const newValue = [...pre, administeredDateFormatted];
        setValue("administeredDates", newValue);
        return newValue;
      });
    }
    setValue("administeredDate", null);
  };

  const administeredDatesRemove = (index) => {
    let cloneArray = Object.assign([], administeredDates);
    cloneArray.splice(index, 1);
    setAdministeredDates(() => {
      setValue("administeredDates", cloneArray);
      return cloneArray;
    });
  };

  const onSubmit = ({
    immunizationName,
    immunizationDescription,
    administeredDates,
    administeredLocation,
    dosage,
    method,
    detailsTags,
    isActive,
  }) => {
    console.log({
      id: params.id,
      immunizationName,
      immunizationDescription,
      administeredDates,
      administeredLocation,
      dosage,
      method,
      detailsTags,
      isActive,
    });
    immunizationPutMutation({
      id: params.id,
      immunizationName,
      immunizationDescription,
      administeredDates,
      administeredLocation,
      dosage,
      method,
      detailsTags,
      isActive,
    });

    reset();
    navigate("/user/hr/immunizations");
  };

  if (data) {
    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexFormContainer headerTitle={"Immunization Details"}>
            <Flex
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="immunizationName"
                defaultValue={data?.immunizationName}
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Immunization Name"
                    {...field}
                    errorMessage={errors.immunizationName?.message}
                    hasError={!!errors.immunizationName}
                  />
                )}
              />
              <Controller
                name="immunizationDescription"
                control={control}
                defaultValue={data?.immunizationDescription}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="Immunization Description"
                    {...field}
                    errorMessage={errors.immunizationDescription?.message}
                    hasError={!!errors.immunizationDescription}
                  />
                )}
              />
              <Label htmlFor="administeredDate">Administered Dates</Label>

              <Controller
                name="administeredDate"
                control={control}
                render={({ field }) => (
                  <Flex>
                    <DatePicker
                      onChange={(value) => field.onChange(value)}
                      value={field.value || null}
                      format="yyyy-MM-dd"
                      oneTap
                      style={{ width: "100%" }}
                      size="lg"
                    />
                    <Button
                      size="small"
                      disabled={!field.value}
                      onClick={() => administeredDatesAdd(field.value)}
                    >
                      Add
                    </Button>
                  </Flex>
                )}
              />

              <Flex gap={9} direction={"column"}>
                {administeredDates?.map((ad, i) => {
                  return (
                    <Flex
                      key={`iep_ad_${ad}_${i}`}
                      alignItems={"center"}
                      backgroundColor={"rgb(233, 249, 252)"}
                      paddingLeft={15}
                      paddingRight={15}
                      paddingTop={9}
                      paddingBottom={9}
                      borderRadius={12}
                    >
                      <Text>{ad}</Text>
                      <Button
                        size="small"
                        colorTheme="overlay"
                        onClick={() => administeredDatesRemove(i)}
                      >
                        X
                      </Button>
                    </Flex>
                  );
                })}
              </Flex>

              <Controller
                name="administeredLocation"
                control={control}
                defaultValue={data?.administeredLocation}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Administered Location"
                    placeholder="Select"
                    {...field}
                    errorMessage={errors.administeredLocation?.message}
                    hasError={!!errors.administeredLocation}
                  >
                    <option value="Left Arm">Left Arm</option>
                    <option value="Right Arm">Right Arm</option>
                    <option value="Left Thigh">Left Thigh</option>
                    <option value="Right Thigh">Right Thigh</option>
                    <option value="Left Buttcheek">Left Buttcheek</option>
                    <option value="Right Buttcheek">Right Buttcheek</option>
                  </SelectField>
                )}
              />
              <Controller
                name="dosage"
                control={control}
                defaultValue={data?.dosage}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Dosage"
                    {...field}
                    value={field.value ?? data?.dosage}
                    errorMessage={errors.dosage?.message}
                    hasError={!!errors.dosage}
                  />
                )}
              />
              <Controller
                name="method"
                control={control}
                defaultValue={data?.method}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Method"
                    placeholder="Select"
                    {...field}
                    errorMessage={errors.method?.message}
                    hasError={!!errors.method}
                  >
                    <option value="Injected">Injected</option>
                    <option value="Oral">Oral</option>
                  </SelectField>
                )}
              />
              <Controller
                name="detailsTags"
                control={control}
                render={({ field }) => {
                  return (
                    <TagsInput
                      placeHolder="Tags"
                      value={field?.value || []}
                      onChange={(tags) => field.onChange(tags)}
                    />
                  );
                }}
              />

              <Controller
                name="isActive"
                control={control}
                defaultValue={data?.isActive}
                render={({ field }) => (
                  <SwitchField
                    label="Is Active"
                    labelPosition="end"
                    {...field}
                    isChecked={field.value === 1}
                    onChange={(event) =>
                      field.onChange(event.target.checked ? 1 : 0)
                    }
                  />
                )}
              />
              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
  }
}
